import { GetUserData, IProfessionalProfileRepository, IUserRepository } from '../repository/interfaces';
import { IConnectUseCase, ConnectSearchCriteria, ConnectUserLocation } from './interfaces';

/**
 * Connect Use Case
 * Handles professional search for the connect flow.
 */
export class ConnectUseCase implements IConnectUseCase {
  constructor(
    private professionalProfileRepo: IProfessionalProfileRepository,
    private userRepo: IUserRepository
  ) {}

  /**
   * Search professionals with filters
   * User Story 5: As a booker, I search professionals by style, date/time window, budget, distance, tags; I can relax constraints when no exact matches
   */
  async searchProfessionals(
    searchCriteria: ConnectSearchCriteria,
    userLocation?: ConnectUserLocation
  ): Promise<{
    professionals: Array<{
      professional: any;
      user: GetUserData;
      availability: Array<{
        date: Date;
        timeSlots: string[];
      }>;
      distance?: number;
      matchScore: number;
    }>;
    totalResults: number;
    relaxedCriteria: boolean;
  }> {
    try {
      const allProfessionals = await this.professionalProfileRepo.getProfessionalProfiles();

      // Filter by availability if specified
      let professionals = allProfessionals;
      if (searchCriteria.availability !== undefined) {
        professionals = professionals.filter(p => p.isAvailable === searchCriteria.availability);
      }

      // Get user data for each professional
      const professionalDetails = [];
      for (const professional of professionals) {
        const user = await this.userRepo.getUserById(professional.userId);
        if (!user) continue;

        // Calculate distance (simplified - would use actual geo calculations)
        let distance: number | undefined;
        if (userLocation && searchCriteria.distance) {
          distance = Math.random() * 50; // Mock distance calculation
        }

        // Filter by distance if specified
        if (searchCriteria.distance && distance && distance > searchCriteria.distance) {
          continue;
        }

        // Calculate match score based on criteria
        let matchScore = 100;

        // Service type match
        if (
          searchCriteria.serviceType &&
          !professional.serviceCategories?.includes(searchCriteria.serviceType) &&
          !professional.services?.includes(searchCriteria.serviceType)
        ) {
          matchScore -= 30;
        }

        // Budget match
        if (searchCriteria.budgetMin && professional.hourlyRate && professional.hourlyRate < searchCriteria.budgetMin) {
          matchScore -= 20;
        }
        if (searchCriteria.budgetMax && professional.hourlyRate && professional.hourlyRate > searchCriteria.budgetMax) {
          matchScore -= 20;
        }

        // Tags match (simplified)
        if (searchCriteria.tags && searchCriteria.tags.length > 0) {
          const tagMatch = searchCriteria.tags.some(tag =>
            professional.danceStyles?.some(style => style.toLowerCase().includes(tag.toLowerCase())) ||
            professional.serviceCategories?.some(category => category.toLowerCase().includes(tag.toLowerCase())) ||
            professional.services?.some(service => service.toLowerCase().includes(tag.toLowerCase()))
          );
          if (!tagMatch) {
            matchScore -= 25;
          }
        }

        // Mock availability for the next 7 days
        const availability = [];
        for (let i = 0; i < 7; i++) {
          const date = new Date();
          date.setDate(date.getDate() + i);
          availability.push({
            date,
            timeSlots: ['09:00', '10:00', '14:00', '15:00', '16:00'] // Mock time slots
          });
        }

        professionalDetails.push({
          professional,
          user,
          availability,
          distance,
          matchScore: Math.max(0, matchScore)
        });
      }

      // Sort by match score
      professionalDetails.sort((a, b) => b.matchScore - a.matchScore);

      // Check if we need to relax criteria
      let relaxedCriteria = false;
      if (professionalDetails.length === 0 && searchCriteria.budgetMax) {
        // Relax budget constraints
        const relaxedResults = await this.searchProfessionals({
          ...searchCriteria,
          budgetMax: searchCriteria.budgetMax * 1.5 // Increase budget by 50%
        }, userLocation);

        if (relaxedResults.professionals.length > 0) {
          relaxedCriteria = true;
          return {
            ...relaxedResults,
            relaxedCriteria
          };
        }
      }

      return {
        professionals: professionalDetails.slice(0, 20), // Limit results
        totalResults: professionalDetails.length,
        relaxedCriteria
      };
    } catch (error) {
      console.error('Error searching professionals:', error);
      throw error;
    }
  }
}
